import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Shield,
  Globe,
  Users,
  Zap,
  CheckCircle,
  ExternalLink,
  AlertCircle,
  Phone,
  Mail,
  MessageCircle,
  Building,
  Smartphone,
  CreditCard,
  Lock
} from 'lucide-react'

const SolutionDetail: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [solution, setSolution] = useState(null)

  // 静态解决方案数据
  const solutions = {
    'enterprise-web3': {
      id: 'enterprise-web3',
      title: '企业Web3数字化转型解决方案',
      category: '企业服务',
      description: '为企业提供完整的Web3数字化转型解决方案，包括区块链集成、数字资产管理、智能合约开发等一站式服务。',
      shortDescription: '企业级Web3数字化转型完整解决方案',
      image: '/images/solutions/enterprise-web3.jpg',
      features: [
        {
          title: '区块链基础设施',
          description: '为企业搭建私有链或联盟链，提供高性能、高安全性的区块链基础设施',
          icon: 'Shield',
          details: [
            '私有链/联盟链搭建',
            '高性能共识算法',
            '企业级安全防护',
            '7x24小时运维支持'
          ]
        },
        {
          title: '数字资产管理',
          description: '安全可靠的数字资产存储、转移和管理系统',
          icon: 'Lock',
          details: [
            '多签钱包管理',
            '冷热钱包分离',
            '资产审计追踪',
            '合规性管理'
          ]
        },
        {
          title: '智能合约开发',
          description: '定制化智能合约开发，满足企业特定业务需求',
          icon: 'Zap',
          details: [
            '业务需求分析',
            '合约架构设计',
            '安全审计测试',
            '部署运维支持'
          ]
        },
        {
          title: 'DeFi集成服务',
          description: '集成主流DeFi协议，为企业提供去中心化金融服务',
          icon: 'CreditCard',
          details: [
            '流动性挖矿',
            '借贷协议集成',
            'DEX交易集成',
            '收益优化策略'
          ]
        }
      ],
      benefits: [
        '降低技术门槛，快速接入Web3生态',
        '提供专业的技术支持和咨询服务',
        '确保合规性和安全性',
        '降低开发和维护成本',
        '提升企业数字化竞争力'
      ],
      useCases: [
        {
          industry: '金融行业',
          description: '为银行、保险、证券等金融机构提供区块链基础设施和数字资产管理服务',
          icon: 'Building'
        },
        {
          industry: '供应链管理',
          description: '通过区块链技术实现供应链透明化、可追溯性管理',
          icon: 'Globe'
        },
        {
          industry: '数字身份',
          description: '为企业员工和客户提供去中心化身份认证和管理服务',
          icon: 'Users'
        },
        {
          industry: '物联网',
          description: '结合物联网设备，实现数据上链和设备管理',
          icon: 'Smartphone'
        }
      ],
      pricing: {
        basic: {
          name: '基础版',
          price: '¥50,000',
          period: '月',
          features: [
            '基础区块链搭建',
            '标准数字钱包',
            '基础技术支持',
            '5个智能合约'
          ]
        },
        professional: {
          name: '专业版',
          price: '¥150,000',
          period: '月',
          features: [
            '高级区块链配置',
            '企业级钱包管理',
            '7x24小时技术支持',
            '20个智能合约',
            'DeFi协议集成',
            '安全审计服务'
          ],
          popular: true
        },
        enterprise: {
          name: '企业版',
          price: '定制报价',
          period: '年',
          features: [
            '完全定制化方案',
            '私有链部署',
            '专属技术团队',
            '无限制智能合约',
            '全生态集成',
            '合规性保障',
            '培训服务'
          ]
        }
      },
      process: [
        {
          step: 1,
          title: '需求分析',
          description: '深入了解企业业务需求，制定定制化解决方案'
        },
        {
          step: 2,
          title: '方案设计',
          description: '设计技术架构和实施方案，确保可行性和安全性'
        },
        {
          step: 3,
          title: '开发实施',
          description: '按照设计方案进行开发和部署，确保质量'
        },
        {
          step: 4,
          title: '测试验收',
          description: '全面测试系统功能，确保稳定性和安全性'
        },
        {
          step: 5,
          title: '上线运维',
          description: '系统正式上线，提供持续的技术支持和维护'
        }
      ]
    }
  }

  useEffect(() => {
    if (id) {
      // 直接使用静态数据，不需要API调用
      const solutionData = solutions[id]
      if (solutionData) {
        setSolution(solutionData)
      } else {
        // 如果找不到解决方案，使用默认解决方案
        setSolution(solutions['enterprise-web3'])
      }
    }
  }, [id])

  const handleContact = () => {
    navigate('/contact?subject=解决方案咨询&solution=' + encodeURIComponent(solution?.title || ''))
  }

  if (!solution) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            解决方案未找到
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            抱歉，您要查看的解决方案不存在
          </p>
          <Link
            to="/solutions"
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回解决方案列表
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{solution.title} - DeepLink Limited</title>
        <meta name="description" content={solution.description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <section className="py-8 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate('/solutions')}
                className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                返回解决方案列表
              </button>
            </div>
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center text-white"
            >
              <h1 className="text-5xl font-bold mb-6">
                {solution.title}
              </h1>
              <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                {solution.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleContact}
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                >
                  立即咨询
                </button>
                <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors">
                  下载方案
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                核心功能
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                为企业提供全方位的Web3技术解决方案
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {solution.features.map((feature, index) => {
                const IconComponent = {
                  'Shield': Shield,
                  'Lock': Lock,
                  'Zap': Zap,
                  'CreditCard': CreditCard
                }[feature.icon] || Shield

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-8"
                  >
                    <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mb-6">
                      <IconComponent className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      {feature.description}
                    </p>
                    <ul className="space-y-3">
                      {feature.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center text-gray-600 dark:text-gray-400">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                解决方案优势
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                为什么选择我们的Web3解决方案
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {solution.benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg text-center"
                >
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">
                    {benefit}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                应用场景
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                适用于多个行业和业务场景
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {solution.useCases.map((useCase, index) => {
                const IconComponent = {
                  'Building': Building,
                  'Globe': Globe,
                  'Users': Users,
                  'Smartphone': Smartphone
                }[useCase.icon] || Building

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-8"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                          {useCase.industry}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {useCase.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                解决方案价格
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                灵活的价格方案，满足不同规模企业需求
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {Object.entries(solution.pricing).map(([key, plan], index) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg relative ${
                    plan.popular ? 'ring-2 ring-blue-600 dark:ring-blue-400' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                        推荐
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900 dark:text-white">
                        {plan.price}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400 ml-2">
                        /{plan.period}
                      </span>
                    </div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-gray-600 dark:text-gray-400">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={handleContact}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                      plan.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    立即咨询
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                实施流程
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                专业的实施流程，确保项目成功交付
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {solution.process.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-blue-600">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                准备开始您的Web3转型之旅？
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                联系我们的专业团队，获取定制化解决方案
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleContact}
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                >
                  立即咨询
                </button>
                <Link
                  to="/contact"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors"
                >
                  联系我们
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default SolutionDetail